// src/pages/Employee/CreateEmployee/buildPayload.ts

import { EmployeeFormData, FileValue } from '../../types/employee.types';

// Appends a value only when it's non-empty, so we don't send empty strings
// for every optional field.
const appendIfPresent = (fd: FormData, key: string, value: string | number | boolean | null | undefined) => {
  if (value === null || value === undefined || value === '') return;
  fd.append(key, String(value));
};

const appendFile = (fd: FormData, key: string, fileValue: FileValue) => {
  if (fileValue.file) fd.append(key, fileValue.file);
};

// Builds a single multipart/form-data payload for POST /employees.
// Nested one-to-many rows (dependents, educations, experiences, certifications)
// are sent as JSON strings under their own keys, with files appended
// separately using an index-based key (e.g. experience_attachment_0) so the
// backend can associate each upload with the right row.
export const buildEmployeePayload = (form: EmployeeFormData): FormData => {
  const fd = new FormData();
  const { basicInfo, personalFamily, address, jobSalaryBank, qualifications, documents, emergencyReference } = form;

  // Basic info
  Object.entries(basicInfo).forEach(([key, value]) => appendIfPresent(fd, key, value));

  // Personal + dependents
  appendIfPresent(fd, 'marital_status', personalFamily.marital_status);
  appendIfPresent(fd, 'spouse_name', personalFamily.spouse_name);
  appendIfPresent(fd, 'age', personalFamily.age);
  fd.append(
    'dependents',
    JSON.stringify(personalFamily.dependents.map(({ _key, ...rest }) => rest)),
  );

  // Address
  const {
    address_proof,
    permanent_same_as_present,
    ...addressFields
  } = address;
  Object.entries(addressFields).forEach(([key, value]) => appendIfPresent(fd, key, value as string));
  appendFile(fd, 'address_proof', address_proof);

  // Job, salary, bank
  // ✅ Handle employee_location_ids separately - convert array to JSON string
  const { employee_location_ids, ...restJobSalaryBank } = jobSalaryBank;
  
  // Handle employee_location_ids - send as JSON string
  if (employee_location_ids && employee_location_ids.length > 0) {
    fd.append('employee_location_ids', JSON.stringify(employee_location_ids));
  } else {
    fd.append('employee_location_ids', JSON.stringify([]));
  }
  
  // Handle other job/salary/bank fields
  Object.entries(restJobSalaryBank).forEach(([key, value]) => {
    if (typeof value === 'boolean') {
      fd.append(key, value ? '1' : '0');
    } else {
      appendIfPresent(fd, key, value);
    }
  });

  // ✅ Handle geofence separately (it was inside restJobSalaryBank)
  // Since we extracted employee_location_ids, geofence is in restJobSalaryBank
  // It will be handled by the loop above since it's a boolean
  
  // Qualifications
  fd.append(
    'educations',
    JSON.stringify(qualifications.educations.map(({ _key, ...rest }) => rest)),
  );

  fd.append(
    'experiences',
    JSON.stringify(
      qualifications.experiences.map(({ _key, attachment, ...rest }) => rest),
    ),
  );
  qualifications.experiences.forEach((exp, idx) => {
    if (exp.attachment.file) fd.append(`experience_attachment_${idx}`, exp.attachment.file);
  });

  fd.append(
    'certifications',
    JSON.stringify(
      qualifications.certifications.map(({ _key, attachment, ...rest }) => rest),
    ),
  );
  qualifications.certifications.forEach((cert, idx) => {
    if (cert.attachment.file) fd.append(`certification_attachment_${idx}`, cert.attachment.file);
  });

  // Documents
  appendIfPresent(fd, 'pan_number', documents.pan_number);
  appendIfPresent(fd, 'epf_uan', documents.epf_uan);
  appendIfPresent(fd, 'esic_number', documents.esic_number);
  appendFile(fd, 'pan_attachment', documents.pan_attachment);
  appendFile(fd, 'aadhar_attachment', documents.aadhar_attachment);
  appendFile(fd, 'epf_attachment', documents.epf_attachment);
  appendFile(fd, 'esic_attachment', documents.esic_attachment);
  appendFile(fd, 'resume', documents.resume);
  appendFile(fd, 'payslip_1', documents.payslip_1);
  appendFile(fd, 'payslip_2', documents.payslip_2);
  appendFile(fd, 'payslip_3', documents.payslip_3);

  // Emergency + reference
  Object.entries(emergencyReference).forEach(([key, value]) => appendIfPresent(fd, key, value));

  return fd;
};